// 2. External libraries
import { redirect } from '@tanstack/react-router';

// 4. Types
import type { RouterContext } from '~/router';
import type { ParsedLocation } from '@tanstack/react-router';

// Key for storing intended destination in sessionStorage
export const RETURN_URL_KEY = 'deaddrop_return_url';

/**
 * Route guard that stores the current URL in sessionStorage BEFORE redirecting
 * to sign-in. This preserves the intended destination through OAuth flows where
 * query params may be lost.
 *
 * Reads the Clerk-derived session from router context. While Clerk is still
 * loading (isLoaded false, isSignedIn undefined) the guard resolves without
 * redirecting, so an already-authenticated user is never bounced to /sign-in
 * during the initial load window. Once auth resolves: when signed out, it
 * records the full path (excluding sign-in related pages) and throws a redirect
 * to /sign-in, forwarding the destination as a returnUrl search param; when
 * signed in, it resolves without redirect.
 *
 * The stored URL is also read back by SsoCallbackPage and MagicLinkCallbackPage
 * after an OAuth round-trip, where search params do not survive.
 */
export function requireSignedIn({
  context,
  location,
}: {
  context: RouterContext;
  location: ParsedLocation;
}): void {
  // Auth has not resolved yet: do not treat the loading window as signed-out.
  if (!context.auth.isLoaded || context.auth.isSignedIn) {
    return;
  }

  // Store the full path including search params (e.g., /telegram/connect?token=xxx)
  const fullPath = location.pathname + location.searchStr;

  // Only store if not already on sign-in related pages
  if (!fullPath.startsWith('/sign-in') && !fullPath.startsWith('/sso-callback')) {
    sessionStorage.setItem(RETURN_URL_KEY, fullPath);
  }

  // oxlint-disable-next-line typescript/only-throw-error -- TanStack Router redirect() is thrown by design
  throw redirect({ to: '/sign-in', search: { returnUrl: fullPath }, replace: true });
}
